/**
 * useTodos.js: To Do page (/todos), the Home "My to-dos" card, the nav badges
 * and the voice note to-do buttons.
 *
 * API is camelCase both ways. No case mapping here: routes/todos.js serializes
 * every response, so bodies and responses pass through untouched.
 *
 * The server owns scope: my list, plus (admins) everyone's assigned to-dos, never
 * anyone's private ones. Every key lives under queryKeys.todos.all and every
 * mutation invalidates it, so one write refreshes the list, the badges and the
 * Home card. Realtime is dead app-wide, so the summary polls every minute.
 *
 * New to-do ids ('todo_' + 32 hex, newTodoId) are made by the caller and passed
 * in the mutation variables, never inside mutationFn: a retried POST then reuses
 * the id and the server answers with the row it already saved.
 *
 * No client logAction (crew get a 403 on the action log); the server logs.
 * track() gets enums only, never a title.
 *
 * Contract: docs/agent-notes/todo-page.md (sections 4.5 and 5).
 */
import { useCallback, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryClient';
import { track } from '../lib/telemetry';
import { applyOrder } from '../lib/todoText';

const K = queryKeys.todos;
const REFRESH = 60 * 1000;

// A refusal (signed out, no access, gone, taken) cannot succeed on retry.
const retryUnlessRefused = (count, err) => {
  if ([401, 403, 404, 409].includes(err?.status)) return false;
  return count < 2;
};

const qs = (params) => {
  const s = new URLSearchParams(
    Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  ).toString();
  return s ? `?${s}` : '';
};

const todoPath = (id) => `/todos/${encodeURIComponent(id)}`;

/** 'todo_' + 32 hex. */
export function newTodoId() {
  const hex = typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID().replace(/-/g, '')
    : Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, '0')).join('');
  return `todo_${hex}`;
}

// ── Queries ─────────────────────────────────────────────────────────────

/**
 * GET /todos?view=mine|team&assignee= -> { today, items }. Open to-dos plus the
 * ones finished in the last 30 days. `mine` comes in the order I dragged it into
 * (new ones on top); `team` (admins) is every assigned to-do.
 */
export function useTodos({ view = 'mine', assignee = '' } = {}, opts = {}) {
  return useQuery({
    queryKey: K.list(view, assignee),
    queryFn: () => api.get(`/todos${qs({ view, assignee })}`),
    retry: retryUnlessRefused,
    refetchInterval: REFRESH,
    ...opts,
  });
}

/** GET /todos/:id -> one to-do, for ?todo= links. 404 when it is gone or not visible. */
export function useTodo(id, opts = {}) {
  return useQuery({
    queryKey: K.one(id),
    queryFn: () => api.get(todoPath(id)),
    retry: retryUnlessRefused,
    ...opts,
    enabled: !!id && (opts.enabled ?? true),
  });
}

/**
 * GET /todos/summary -> { today, openCount, overdueCount, dueTodayCount, items }.
 * Counts are my open to-dos; items are my overdue and due-today ones (Home card).
 * Pass enabled:false for external users.
 */
export function useTodoSummary({ enabled = true } = {}) {
  return useQuery({
    queryKey: K.summary,
    queryFn: () => api.get('/todos/summary'),
    retry: retryUnlessRefused,
    refetchInterval: REFRESH,
    enabled,
  });
}

/** GET /todos/people -> [{ id, name, role }] (admins): who a to-do can be assigned to. */
export function useTodoPeople(opts = {}) {
  return useQuery({
    queryKey: K.people,
    queryFn: async () => {
      const res = await api.get('/todos/people');
      return Array.isArray(res) ? res : res?.items || [];
    },
    retry: retryUnlessRefused,
    staleTime: 5 * 60 * 1000,
    ...opts,
  });
}

/** GET /todos/suggestions -> { today, items: [{ key, kind, title, detail, link }] } (admins). */
export function useTodoSuggestions(opts = {}) {
  return useQuery({
    queryKey: K.suggestions,
    queryFn: () => api.get('/todos/suggestions'),
    retry: retryUnlessRefused,
    ...opts,
  });
}

/**
 * GET /todos/voicenote-items?ids= -> { items: [{ sourceId, sourceItem, todoId, done }] }:
 * which voice note items I already made a to-do from. Up to 200 note ids.
 */
export function useVoicenoteTodoItems(noteIds, opts = {}) {
  const ids = [...new Set(noteIds || [])].sort().slice(0, 200).join(',');
  return useQuery({
    queryKey: K.voicenoteItems(ids),
    queryFn: () => api.get(`/todos/voicenote-items${qs({ ids })}`),
    retry: retryUnlessRefused,
    ...opts,
    enabled: !!ids && (opts.enabled ?? true),
  });
}

// ── Mutations ───────────────────────────────────────────────────────────

function useInvalidateTodos() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: K.all });
}

/**
 * POST /todos. Variables: { id (newTodoId(), made by the caller), title, notes?,
 * dueDate?, assigneeId?, link?: { type, id }, source?, sourceId?, sourceItem? }.
 * Resolves to the to-do plus `assigneeCanOpen` when it is assigned to someone else and linked.
 * Report errors with mutateAsync().catch (see useUpdateTodo).
 */
export function useCreateTodo() {
  const invalidate = useInvalidateTodos();
  return useMutation({
    mutationFn: (input) => api.post('/todos', input),
    onSuccess: (todo, input) => track('todos.created', {
      source: input?.source || 'manual',
      assigned: !!todo && todo.isPrivate === false,
    }),
    onSettled: invalidate,
  });
}

/** Run `fn` over every cached to-do (lists, the summary, one to-do, voice note items). */
function updateCached(qc, fn) {
  // Lists and the summary hold { items }, one to-do is the row itself; people is an array.
  qc.setQueriesData({ queryKey: K.all }, (old) => {
    if (!old || typeof old !== 'object' || Array.isArray(old)) return old;
    if (Array.isArray(old.items)) return { ...old, items: old.items.map(fn) };
    return fn(old);
  });
}

/** Set `done` on every cached copy of one to-do. */
function setCachedDone(qc, id, done, completedAt) {
  updateCached(qc, (t) => {
    if (t?.id === id) return { ...t, done, completedAt };
    if (t?.todoId === id) return { ...t, done };
    return t;
  });
}

const MONEY_KEY = ['todos', 'money'];
// Every write of the $ mark to a to-do that exists: row toggles and dialog saves.
const MONEY_SCOPE = { id: 'todos.money' };

/** Set `money` (the $ mark) on every cached copy of one to-do. */
function setCachedMoney(qc, id, money) {
  updateCached(qc, (t) => (t?.id === id ? { ...t, money } : t));
}

/**
 * PUT /todos/:id with only the changed fields: { id, title?, notes?, dueDate?, done?,
 * money?, assigneeId?, link?, was? } (null clears dueDate and link). `was` = the values the
 * editor started from for the fields it sends; the server answers 409 todo_changed
 * when one of them was saved by someone else meanwhile. A `done` change shows at once
 * everywhere the to-do is cached and rolls back if the server refuses it.
 *
 * Callers report errors with mutateAsync().catch, not mutate(vars, { onError }): a
 * second tick on the same hook drops the first call's callbacks (TanStack v5).
 *
 * { withMoney: true } (the dialog, whose save can carry `money`): the save joins the
 * $ toggles' scope, so it waits for a row's $ still saving and lands after it.
 * Without it, a slow or retried toggle clicked before the dialog opened landed
 * after the dialog's "Saved." and put the mark back (review round 2 of the $ mark).
 * Row ticks stay out of it, so quick ticks on several rows still save side by side.
 */
export function useUpdateTodo({ withMoney = false } = {}) {
  const qc = useQueryClient();
  return useMutation({
    scope: withMoney ? MONEY_SCOPE : undefined,
    mutationFn: ({ id, ...patch }) => api.put(todoPath(id), patch),
    onMutate: async ({ id, ...patch }) => {
      if (typeof patch.done !== 'boolean') return undefined;
      await qc.cancelQueries({ queryKey: K.all });
      const cached = qc.getQueriesData({ queryKey: K.all })
        .flatMap(([, data]) => (Array.isArray(data?.items) ? data.items : [data]))
        .find((t) => t?.id === id);
      setCachedDone(qc, id, patch.done, patch.done ? new Date().toISOString() : null);
      return { completedAt: cached?.completedAt ?? null };
    },
    // Roll back only this to-do: another tick that overlapped it may have saved.
    onError: (_err, { id, done }, ctx) => {
      if (typeof done === 'boolean') setCachedDone(qc, id, !done, done ? null : ctx?.completedAt ?? null);
    },
    onSuccess: (_todo, { done }) => {
      if (typeof done === 'boolean') track('todos.done', { done });
    },
    // Not awaited: the dialog closes on the server's answer, before a refetch can
    // drop the to-do from the open list (reassigned away) underneath it.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: K.all });
    },
  });
}

/**
 * PUT /todos/:id { money } from a row's $ (To Do page, Home card). The mark shows
 * at once everywhere the to-do is cached; a refusal puts back only this to-do's
 * mark (another toggle that overlapped it may have saved). Only the last toggle to
 * settle refetches, so a quick second click never flickers back. The dialog saves
 * the mark with its other fields through useUpdateTodo({ withMoney: true }) instead
 * (not optimistic), in the same scope.
 * Toggles save one at a time, in click order (one mutation scope): each sends the
 * value, not a flip, so a retried earlier toggle that landed after a later one
 * saved the opposite of the last click (review round 1).
 * Variables: { id, money }. Callers toast errors with mutateAsync().catch.
 */
export function useSetTodoMoney() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: MONEY_KEY,
    scope: MONEY_SCOPE,
    mutationFn: ({ id, money }) => api.put(todoPath(id), { money }),
    onMutate: async ({ id, money }) => {
      await qc.cancelQueries({ queryKey: K.all });
      setCachedMoney(qc, id, money);
    },
    onError: (_err, { id, money }) => setCachedMoney(qc, id, !money),
    onSuccess: (_todo, { money }) => track('todos.money', { money }),
    onSettled: () => {
      if (qc.isMutating({ mutationKey: MONEY_KEY }) <= 1) qc.invalidateQueries({ queryKey: K.all });
    },
  });
}

const ORDER_KEY = ['todos', 'order'];

/**
 * PUT /todos/order { ids } (204): my open to-dos, top first, after a drag.
 * Returns { save(ids), pendingOrder }. save(ids) takes the whole open Mine list
 * in its new order; call it from the drop handler. Drags save one at a time, in
 * order (one mutation scope); the cache gets each order at once (Home card,
 * badge), and only the last save to finish refetches.
 *
 * pendingOrder is the last order dropped, until its save settles; the page shows
 * the Mine list through it (applyOrder). It is set in the drop's own render: the
 * cache's update lands a task later, so a drop drawn from the cache sent the row
 * back to its old place first and then flew it across the page. And it holds over
 * any refetch that lands before the save does (a tick, the poll, focus): that
 * refetch reads the old order, and a drag made on it overwrote the first one
 * (both review round 2). When the last save settles, its order goes back into the
 * cache before pendingOrder clears, so nothing flashes.
 *
 * A failed save puts the list back and calls `onFailed` (the caller toasts), but
 * only when no later drag is still saving. A later drag was made on top of this
 * one's order and sends the whole list, so its save covers this one: the order
 * stays and nothing is reported. Errors never reach the caller.
 */
export function useReorderTodos({ onFailed } = {}) {
  const qc = useQueryClient();
  const key = K.list('mine', '');
  const last = () => qc.isMutating({ mutationKey: ORDER_KEY }) <= 1;
  const [pendingOrder, setPendingOrder] = useState(null);
  const { mutate } = useMutation({
    mutationKey: ORDER_KEY,
    scope: { id: 'todos.order' },
    mutationFn: ({ ids }) => api.put('/todos/order', { ids }),
    onMutate: async ({ ids }) => {
      await qc.cancelQueries({ queryKey: K.all });
      const prev = qc.getQueryData(key);
      if (Array.isArray(prev?.items)) qc.setQueryData(key, { ...prev, items: applyOrder(prev.items, ids) });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (!last()) return;
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      onFailed?.();
    },
    onSuccess: () => track('todos.reordered'),
    onSettled: (_data, err, { ids }) => {
      if (!last()) return;
      if (!err) {
        qc.setQueryData(key, (old) => (Array.isArray(old?.items) ? { ...old, items: applyOrder(old.items, ids) } : old));
      }
      setPendingOrder(null);
      qc.invalidateQueries({ queryKey: K.all });
    },
  });
  const save = useCallback((ids) => {
    setPendingOrder(ids);
    mutate({ ids });
  }, [mutate]);
  return { save, pendingOrder };
}

/** DELETE /todos/:id (hard delete). Deleting an accepted suggestion's to-do also dismisses it. */
export function useDeleteTodo() {
  const invalidate = useInvalidateTodos();
  return useMutation({
    mutationFn: (id) => api.del(todoPath(id)),
    onSuccess: () => track('todos.deleted'),
    // Not awaited, like useUpdateTodo.
    onSettled: () => {
      invalidate();
    },
  });
}

/**
 * POST /todos/suggestions/accept { key } -> the new to-do (mine, due today).
 * 404 suggestion_gone and 409 suggestion_taken carry a sentence to show as is.
 * Variables: { key, kind } (kind is only for telemetry).
 */
export function useAcceptSuggestion() {
  const invalidate = useInvalidateTodos();
  return useMutation({
    mutationFn: ({ key }) => api.post('/todos/suggestions/accept', { key }),
    onSuccess: (_todo, { kind }) => track('todos.suggestion', { kind, action: 'accept' }),
    onSettled: invalidate,
  });
}

/** POST /todos/suggestions/dismiss { key }. It never comes back for me. Variables: { key, kind }. */
export function useDismissSuggestion() {
  const invalidate = useInvalidateTodos();
  return useMutation({
    mutationFn: ({ key }) => api.post('/todos/suggestions/dismiss', { key }),
    onSuccess: (_res, { kind }) => track('todos.suggestion', { kind, action: 'dismiss' }),
    onSettled: invalidate,
  });
}
